import { useCallback, useMemo } from "react";
import { type SrpProductData } from "../../../../../utils/models";

import { Card } from "../../../../../Configurators/Linear/components/Card";
import {
  AliasesParameter,
  GPSDParameter,
  OldDesignationParameter,
  PackagingParameter,
  SchaefflerIdParameter,
} from "../../../../../common/components/Product/ProductParams";
import { QuantityInput } from "../../../../../common/components/QuantityInput/QuantityInput";
import { MAX_QUANTITY } from "../../../../../utils/constants";
import CircleSpinner from "../../../../../common/components/CircleSpinner/CircleSpinner";
import {
  getPackagingInformation,
  isProductPMA,
} from "../../../../../feature/ProductDescriptor/v1/productDescriptorUtils";

import { IconType } from "../../../../../common/components/icon/IconType";
import { useAddProductToWishlistFlow } from "../../../../../common/hooks/useAddProductToWishlistFlow";
import { IconSize } from "../../../../../common/components/icon/IconSize";
import { Icon } from "../../../../../common/components/icon/Icon";
import { getCanUserAddToCart } from "../../../../../ProductDetails/components/ProductSummary/ExtendedPdpSummary";
import {
  isProductComparisonCheckboxVisible,
  ProductComparisonCheckbox,
} from "../../../../../ProductComparison/ProductComparisonCheckbox";
import { NewVersionMessage } from "../../../../../common/components/Messages/NewVersionMessage";
import { trackUserClickRecommendedProductSectionDetails } from "../../../../../ProductDetails/RecommendedProducts/recommendedProductAnalytics";

import { useTranslation } from "react-i18next";
import { SRPPackageRestrictionContext } from "~/common/components/PackageRestrictionContext/SRPPackageRestrictionContext";
import { calculateSalesCampaign4CampaignPage, useSelectPricingDetails } from "~/SalesCampaign/utils";
import { getGALocationForSearchItem, useSearchPageType } from "~/Search/useSearchPageType";
import { SaleTag } from "~/SalesCampaign/components/SaleTag/SaleTag";
import { SaleQuantityIndicator } from "~/SalesCampaign/components/SaleQuantityIndicator/SaleQuantityIndicator";

import classes from "./SearchResultProductCustomer.module.less";
import { Price, type PriceProps } from "~/common/components/Price/v3/Price";
import { SaleDiscountIndicator } from "~/SalesCampaign/components/SaleDiscountIndicator/SaleDiscountIndicator";
import { makeSalesAwarePackCodeProvider } from "~/SalesCampaign/salesAwarePackCodeProvider";
import { SalesDataProvider } from "~/SalesCampaign/useSalesData";
import { ProductDescriptorContext } from "~/feature/Product/ProductContext";
import { Product } from "~/feature/Product";
import { Box, Button, Divider, Stack, styled, Typography } from "@mui/material";
import { Alert } from "~/common/m3/Alert/Alert";
import { useResultTileZoovuParams } from "~/Search/utils/searchUtils";
import {
  useAddToCartForm,
  usePackagePreselectionEffect,
  usePackageSizeSyncEffect,
} from "~/AddToCart/hooks/useAddToCartCampaign";
import { ProductService } from "~/AddToCart/ProductService";
import type { ProductDescriptor } from "~/feature/ProductDescriptor/v1/types";
import type { AvailabilityInfo } from "~/api/pna/AvailabilityInfo";
import type { PackagingInformationEntry, WarehouseStockData } from "~/models/ProductData";
import { toMapFn } from "~/utils/reducers";
import { addToCartApi } from "~/AddToCart/redux/addToCart/actions";
import { trackPdpWishlistClick } from "~/feature/Analytics/ga/ga";
import { isTariffSurchargeEnabled } from "~/Cart/cartUtils";
import RichTooltip from "~/common/components/RichTooltip/RichTooltip";
import Cycle from "~/common/icons/Cycle";
import parse from "html-react-parser";
import ProductMMD from "~/feature/Product/elements/ProductMMD";
import MMDAssistant from "~/ProductDetails/components/MMDAssistant/MMDAssistant";
import { FontSize, getFontStyle } from "~/common/components/materialInput/Fonts";
import { SaleEndIndicator } from "~/SalesCampaign/components/SaleEndIndicator/SaleEndIndicator";
import { URL_RECONDITIONABLE } from "~/utils/url/staticUrls";
import Tag from "~/common/components/Tag/Tag";
import { renderButtons } from "./components/useSrpButtons";

interface Props {
  isPdp?: boolean;
  productCode: string;
  index: number;
  onProductClick: (area: string, product: SrpProductData) => void;
  searchQuery: string;
}

export const SearchResultListItemCampaign = ({ productCode, onProductClick, isPdp, index }: Props) => {
  const { t } = useTranslation();

  const pageType = useSearchPageType();
  const gaLocation = getGALocationForSearchItem(isPdp, pageType);

  const onWishlistBtnClick = () => {
    trackPdpWishlistClick(productDescriptor.base.designation, 1);
    openAddToWishlist();
  };

  const formProps = useAddToCartForm({
    productCode,
  });

  const priceReference = formProps.watch("priceReference");

  const originalProductDescriptor = ProductService.useProductDescriptor({
    productCode,
    priceReference,
    options: { fetch: true, fetchAvailability: false },
    // options: { fetch: true, forceFetchBasicData: true, fetchAvailability: false },
  });

  const productDescriptor = useMemo(() => {
    return constructAvailability(originalProductDescriptor);
  }, [originalProductDescriptor]);

  // Select the initial package if there is none
  usePackagePreselectionEffect(false, productDescriptor, formProps, makeSalesAwarePackCodeProvider(null, false));

  // Until the user changes quantity manually, keep it in sync with package size
  usePackageSizeSyncEffect(false, productDescriptor, formProps);

  const product = productDescriptor.base;

  const firstSalesCampaignObj = Object.values(productDescriptor.base.salesCampaign ?? {})?.[0]?.[0];
  const salesCampaignCode = firstSalesCampaignObj?.code;

  const { status: salesStatus, data } = useMemo(
    () => calculateSalesCampaign4CampaignPage({ productDescriptor, salesCampaignCode }),
    [productDescriptor, salesCampaignCode],
  );

  const salePerPack = data?.salePerPack;

  const packCode = formProps.watch("packCode");
  const selectedPack = getPackagingInformation(productDescriptor, packCode);
  const packSize = selectedPack?.packSize ?? 1;

  const salesData = salePerPack?.[packCode];
  const hasManyPackages = salePerPack ? Object.keys(salePerPack).length > 1 : false;

  const quantity = formProps.watch("quantity");
  const [openAddToWishlist, addToWishlistModal] = useAddProductToWishlistFlow({
    productInput: product,
    initialQuantity: quantity,
    initialPriceReference: priceReference,
  });

  const productInformationClick = useCallback(
    () => onProductClick("Product information", product),
    [onProductClick, product],
  );

  let isPackageSizeInvalid = false;
  if (selectedPack) {
    isPackageSizeInvalid = quantity === 0 || quantity % selectedPack.packSize > 0;
  }

  const userCanAddToCart = getCanUserAddToCart(productDescriptor, selectedPack);

  let exceedsDiscontinuedQuantity = false;
  if (salesData) {
    exceedsDiscontinuedQuantity = salesData.isDiscontinued && quantity > salesData.regularStock;
  }

  const webservicesDisabled = window.SCHA.config.disableAllWS;
  const addToCartDisabled =
    webservicesDisabled ||
    !userCanAddToCart ||
    isPackageSizeInvalid ||
    productDescriptor.isLoading ||
    productDescriptor.isError ||
    exceedsDiscontinuedQuantity;

  const hasSales = data?.hasSales;
  const someSaleHasStock = Object.values(data?.salePerPack ?? {}).some((sale) => !sale.soldOut);
  const tagColour = someSaleHasStock ? "active" : "inactive";

  const packaging = getPackagingInformation(productDescriptor, packCode);

  const { price, oldPrice, netPrice, salesPrice, discountFormatted } = useSelectPricingDetails({
    packCode,
    quantity,
    priceReference,
    productCode,
    salesData,
    saleActivated: true,
  });

  const isSoldOut = salesData?.soldOut ?? false;
  const salesStock = salesData?.salesStock ?? 0;
  const exceedsSaleQty = salesStock > 0 && quantity > salesStock && !isSoldOut;

  const hasNotifications = isPackageSizeInvalid || isSoldOut || exceedsSaleQty;

  const tariffSurcharge = isTariffSurchargeEnabled() && netPrice?.tariffSurcharge;

  const linkProps = {
    onClick: productInformationClick,
  };

  const doAddToCart = async () => {
    const formValues = formProps.getValues();

    if (isPdp) {
      trackUserClickRecommendedProductSectionDetails(product.designation);
    }

    await ProductService.getProductDescriptor(productCode, priceReference, { fetch: true }).promise;

    addToCartApi({
      restrictionQuery: "",
      ...formValues,
      selectedPackCode: formValues.packCode,
      addToCartSource: "unknown",
      selectedPack: selectedPack!,
      netPrice,
      optionalParts: {},
      priceUnit: netPrice?.unit ?? 1,
      surcharge: netPrice?.surcharge ?? 0,
      // Will sync inside
      totalQuantity: 1,
    });
  };

  const buttons = renderButtons(productDescriptor, {
    atc: {
      onClick: doAddToCart,
      disabled: addToCartDisabled,
    },
    wishlist: {
      onClick: onWishlistBtnClick,
      sizeVariant: "side",
    },
  });
  buttons.reverse();

  return (
    <SRPPackageRestrictionContext productCode={productCode}>
      <SalesDataProvider value={data}>
        <ProductDescriptorContext value={productDescriptor}>
          {addToWishlistModal}
          {product.displayMMDAssistant && (
            <MMDAssistant
              isPDP={false}
              marketDesignation={product.marketDesignation}
              initialData={{
                quantity: quantity,
                priceReference: priceReference,
              }}
            />
          )}
          <ItemRoot
            className={"jsSearchResultProduct"}
            data-test-id={`search-result-item`}
            {...useResultTileZoovuParams(product)}
          >
            {hasSales && <FloatingSaleTag variant={tagColour} />}

            <Box sx={{ alignSelf: "flex-start" }}>
              <Product.PdpLink onClick={productInformationClick}>
                <Product.PrimaryImageWithLogos clickable={false} />
              </Product.PdpLink>
            </Box>

            <Stack gap={2} flex={1}>
              <Stack gap={0.5}>
                <Box display="flex" justifyContent="space-between">
                  <Product.FullDesignation className={classes.designation} linkProps={linkProps} />
                  <SaleEndIndicator.V2 endDate={salesData?.endDate} />
                </Box>
                {product.marketDesignation && <ProductMMD marketDesignation={product.marketDesignation} />}
                <Product.ShortDescription clickable={false} />
              </Stack>
              <Stack className={classes.parameters} gap={1}>
                <OldDesignationParameter variant="horizontal" label={t("product.oldDesignations")} />
                <SchaefflerIdParameter variant="horizontal" />
                <GPSDParameter variant="horizontal" product={productDescriptor.base} />
                <AliasesParameter variant="horizontal" mode={"13-digits"} />
                {!hasManyPackages && packaging && (
                  <PackagingParameter
                    variant="horizontal"
                    productDescriptor={productDescriptor}
                    packagingInfo={packaging}
                  />
                )}
              </Stack>

              {product.reconditionable && (
                <RichTooltip
                  content={
                    <Typography>
                      {parse(t("cmsnavigationnode.schaefflerContentCatalog.reconditionableInfo"))}
                    </Typography>
                  }
                  actions={[
                    <Button href={URL_RECONDITIONABLE} variant="text">
                      {t("cmsnavigationnode.schaefflerContentCatalog.moreInfo")}
                    </Button>,
                  ]}
                >
                  <Tag icon={<Cycle fontSize="small" />} iconPosition="start" variant="success">
                    {t("cmsnavigationnode.schaefflerContentCatalog.reconditionable")}
                  </Tag>
                </RichTooltip>
              )}

              {isProductComparisonCheckboxVisible(product) && (
                <ProductComparisonCheckbox productCode={productCode} gaLocation={gaLocation} />
              )}

              <NewVersionMessage baseProduct={product} />

              {hasNotifications && (
                <Stack gap={1}>
                  {isPackageSizeInvalid && (
                    <p className={"f-error-red m-0"}>
                      <Icon className={"icon-margin"} type={IconType.InformationCircle} size={IconSize.FONT_20} />
                      {t("addToCartPopup.mismatchedQty")}
                    </p>
                  )}
                  {isSoldOut && (
                    <Alert severity="info">
                      {salesData.fullySoldOut
                        ? t("feature.salesCampaign.soldOut.full")
                        : t("feature.salesCampaign.soldOut.partial")}
                    </Alert>
                  )}
                  {exceedsSaleQty && <Alert severity="info">{t("feature.salesCampaign.exceedsQty")}</Alert>}
                </Stack>
              )}
            </Stack>

            <Divider orientation="vertical" flexItem />

            <Stack gap={2} sx={{ width: "326px" }}>
              {productDescriptor.isLoading && <CircleSpinner />}

              {!isProductPMA(productDescriptor) && (
                <Stack gap={0}>
                  <Stack direction="row" gap={1}>
                    <CurrentPrice
                      price={price}
                      zeroText={t("product.priceOnRequest")}
                      tariffSurcharge={tariffSurcharge}
                      soldOut={isSoldOut && !!oldPrice}
                    />
                    <SaleDiscountIndicator.V2
                      soldOut={isSoldOut}
                      discountFormatted={discountFormatted}
                      variant="titleLarge"
                    />
                  </Stack>

                  {oldPrice && <OldPrice price={oldPrice} soldOut={isSoldOut} />}

                  <Price.NetPriceUnit price={netPrice} variant={"full"} surchargeEnabled={false} />
                  {tariffSurcharge && (
                    <Typography variant="labelSmall">{t("price.tariff.surcharge.applicable")}</Typography>
                  )}
                </Stack>
              )}

              {salesData && <SaleQuantityIndicator.V2 quantity={salesData.salesStock} />}

              <QuantityInput
                variant="compact"
                min={packSize}
                max={MAX_QUANTITY}
                value={quantity}
                disabled={
                  productDescriptor.isAvailabilityLoading ||
                  productDescriptor.isAvailabilityError ||
                  !product.quantityVisible
                }
                step={packSize}
                data-test-id={"plp-srp-list-item-input-quantity"}
                onValueChange={(value) =>
                  formProps.setValue("quantity", value, { shouldTouch: true, shouldDirty: true })
                }
                error={isPackageSizeInvalid}
              />

              <Stack direction="row" gap={2}>
                {buttons}
              </Stack>
            </Stack>
          </ItemRoot>
        </ProductDescriptorContext>
      </SalesDataProvider>
    </SRPPackageRestrictionContext>
  );
};

function constructAvailability(product: ProductDescriptor): ProductDescriptor {
  const salesByCode = product.base.salesCampaign;
  const fifteenDigitByCode = product.base.fifteenDigit;

  if (
    !fifteenDigitByCode ||
    Object.keys(fifteenDigitByCode).length === 0 ||
    !salesByCode ||
    Object.keys(salesByCode).length === 0
  ) {
    return product;
  }

  const packagingInformation: PackagingInformationEntry[] = Object.keys(fifteenDigitByCode).map((packCode) => {
    const salesData = salesByCode[packCode] ?? [];
    // if (salesData.length > 1) {
    //   throw new Error("Multiple sales campaigns are not supported");
    // }
    // const salesDataEntry = salesData[0];
    const fifteenDigitEntry = fifteenDigitByCode[packCode];
    return {
      totalWarehouse: EMPTY_WAREHOUSE,
      global: EMPTY_WAREHOUSE,
      warehouses: [],
      packCode,
      packSize: fifteenDigitEntry.packSize,
      preferredPackQuantity: null,
      primaryWarehouseCode: "",
      primaryWarehouseName: "",
      replenishmentLeadTimeDate: null,
      salesStock: toMapFn(
        salesData,
        (sale) => sale.code,
        (sale) => sale.quantity,
      ),
    };
  });
  const availability: AvailabilityInfo = {
    code: product.base.code,
    hasPackaging: true,
    isFreshPnaCall: false,
    sapMaterialNumber: product.base.sapMaterialNumber,
    packagingInformation,
  };

  return {
    ...product,
    availability,
    isLoaded: product.prices.length > 0,
    isLoading: product.isPriceLoading,
  };
}

const EMPTY_WAREHOUSE: WarehouseStockData = {
  code: "",
  name: "",
  inClarification: 0,
  inProduction: [],
  inStock: [],
};

const ItemRoot = styled(Card)({
  padding: "16px",
  display: "flex",
  gap: "16px",
});

const FloatingSaleTag = styled(SaleTag)({
  position: "absolute",
  top: "0",
  left: "0",
});

const CurrentPrice = styled(Price, {
  shouldForwardProp: (prop) => prop !== "soldOut",
})<PriceProps & { soldOut?: boolean }>(({ soldOut, theme }) => ({
  ...getFontStyle(FontSize.HeadlineSmall),
  fontWeight: soldOut ? "normal" : "bold",
  color: soldOut ? theme.palette.text.secondary : theme.palette.text.primary,
  textDecoration: soldOut ? "line-through" : "none",
}));

const OldPrice = styled(Price, {
  shouldForwardProp: (prop) => prop !== "soldOut",
})<PriceProps & { soldOut?: boolean }>(({ soldOut, theme }) => ({
  color: soldOut ? theme.palette.text.primary : theme.palette.text.secondary,
  fontWeight: soldOut ? "bold" : "normal",
  textDecoration: soldOut ? "none" : "line-through",
}));
